import React, { useState } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Heading, Input, List, ListItem, Stack } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faCircle } from '@fortawesome/pro-regular-svg-icons';
import { Title } from '../../../atoms/Title';

export interface NewPasswordProps {
  password: string;
  errors: Array<string>;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordSubmit: () => void;
}

const NewPassword = (props: NewPasswordProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [validationEnabled] = useState(true);

  // eslint-disable-next-line no-useless-escape
  const hasAtLeast8Chars = props.password.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(props.password);
  const hasNumber = /\d/.test(props.password);
  const hasSymbol = /[=+\-^$*.[\]{}()?"!@#%&/,><\\':;|_~`]+/.test(props.password);
  // check has leading or trailing spaces
  const hasNoLeadingOrTrailingSpaces = !/^\s+|\s+$/g.test(props.password);
  const isPasswordValid = hasAtLeast8Chars && hasLetter && hasNumber && hasSymbol && hasNoLeadingOrTrailingSpaces;

  // console.log('password in new-password comp', props.password);
  // console.log('hasAtLeast8Chars', hasAtLeast8Chars);
  // console.log('hasLetter', hasLetter);
  // console.log('hasNumber', hasNumber);
  // console.log('hasSymbol', hasSymbol);
  // console.log('hasNoLeadingOrTrailingSpaces', hasNoLeadingOrTrailingSpaces);
  // console.log('isPasswordValid', isPasswordValid);

  const RequirementItem = props => {
    const { type, label } = props;
    const getRequirementMet = (validation: boolean) => {
      if (validationEnabled) {
        return validation
          ? {
              color: 'green.400',
              icon: faCircleCheck,
              description: 'Requirement met'
            }
          : {
              color: 'red.400',
              icon: faCircleXmark,
              description: 'Requirement not met'
            };
      }
      return {
        color: 'muted',
        icon: faCircle,
        description: ''
      };
    };

    return (
      <ListItem key={`type-${type}`} fontSize="sm" mb="1" color="muted">
        <Box as="span" mr="1" color={getRequirementMet(type).color}>
          <FontAwesomeIcon icon={getRequirementMet(type).icon} title={getRequirementMet(type).description} fixedWidth />
        </Box>
        {label}
      </ListItem>
    );
  };

  const onPasswordSubmit = evt => {
    evt.preventDefault();
    props.onPasswordSubmit();
  };

  const title = 'Create a new password';

  return (
    <form onSubmit={onPasswordSubmit}>
      <Title title={title} />

      <Heading as="h1" mb="6">
        {title}
      </Heading>

      {props.errors && props.errors.length > 0 && (
        <Alert status="error" mb="4">
          {[props.errors]}
        </Alert>
      )}
      <FormControl mb="4">
        <FormLabel fontSize="lg" htmlFor="new-password" display="flex" justifyContent="space-between" mr="0">
          New password
          <Button
            onClick={() => {
              setShowPassword(showPassword => !showPassword);
            }}
            size="sm"
            height="7"
            variant="ghost"
          >
            {showPassword ? 'Hide' : 'Show'} password
          </Button>
        </FormLabel>

        <Input
          id="new-password"
          autoComplete="new-password"
          size="lg"
          type={showPassword ? 'text' : 'password'}
          onChange={props.onPasswordChange}
          value={props.password}
          isRequired
          data-sentry-mask
        />
      </FormControl>

      {/* {!changePasswordRequestSent ? (
      <Text fontSize="sm" mb="2">
        Please ensure your password meets these requirements:
      </Text>
    ) : null} */}

      <List mb="6" key={`requirements-${props.password}`}>
        <RequirementItem type={hasAtLeast8Chars} label="At least 8 characters" />
        <RequirementItem type={hasLetter} label="At least 1 letter" />
        <RequirementItem type={hasNumber} label="At least 1 number" />
        <RequirementItem type={hasSymbol} label="At least 1 symbol like !@#$%^" />
        <RequirementItem type={hasNoLeadingOrTrailingSpaces} label="No spaces before or after" />
      </List>

      <Stack spacing="3">
        <Button id="create-password" type="submit" isDisabled={!isPasswordValid} size="lg" variant="solid">
          Create password
        </Button>

        <Button as="a" href="/" variant="ghost" size="lg">
          Return to login form
        </Button>
      </Stack>
    </form>
  );
};

export default NewPassword;
